/**
 * KeyDocumentsPanel — eReg's management surface for key-document slots (§3.2)
 * and the "used by" module chips (§3.7). Rendered on the Folders page when a
 * study is selected. Cross-module consumers see the read-only counterpart
 * (ui/StudyDocumentsCard); pinning/unpinning happens only here in eReg.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Pin, PinOff, Loader2, Link2 } from 'lucide-react';
import * as eregApi from '../lib/api';
import type { KeyDocumentSlot, StudyLinks } from '../lib/api';
import { useToast, ConfirmModal, Modal, fieldCls, primaryBtnCls, secondaryBtnCls } from '../../ui';
import { STUDY_ROLE_LABELS } from '../../ui/studyRoles';

function usedByChips(links: StudyLinks | null) {
  if (!links) return [];
  const chips: string[] = [];
  for (const s of links.crm || []) chips.push(`CRM: ${s.title || s.slug}`);
  if (links.organizer && (links.organizer.team_members > 0 || links.organizer.bookmarks > 0)) {
    chips.push(`Organizer: ${links.organizer.team_members} team · ${links.organizer.bookmarks} bookmarks`);
  }
  if ((links.ixverify || []).length > 0) chips.push(`IXVerify: ${links.ixverify.length} linked`);
  if ((links.status_board || []).length > 0) chips.push(`Status Board: ${links.status_board.length} mapped names`);
  if (links.intelligence_documents > 0) chips.push(`Intelligence: ${links.intelligence_documents} docs`);
  return chips;
}

export function KeyDocumentsPanel({ protocolId, refreshToken = 0, onChanged }: {
  protocolId: string;
  /** Parent bumps this after pinning from a document row. */
  refreshToken?: number;
  onChanged?: () => void;
}) {
  const { toast } = useToast();
  const [slots, setSlots] = useState<KeyDocumentSlot[]>([]);
  const [roleLabels, setRoleLabels] = useState<Record<string, string>>(STUDY_ROLE_LABELS);
  const [links, setLinks] = useState<StudyLinks | null>(null);
  const [loading, setLoading] = useState(true);
  const [unpinSlot, setUnpinSlot] = useState<KeyDocumentSlot | null>(null);
  const [unpinning, setUnpinning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [keyDocs, studyLinks] = await Promise.all([
        eregApi.getKeyDocuments(protocolId),
        eregApi.getStudyLinks(protocolId).catch(() => null),
      ]);
      setSlots(keyDocs.slots || []);
      setRoleLabels({ ...STUDY_ROLE_LABELS, ...(keyDocs.role_labels || {}) });
      setLinks(studyLinks);
    } catch (err) {
      console.error('Failed to load key documents:', err);
    } finally {
      setLoading(false);
    }
  }, [protocolId]);

  useEffect(() => { load(); }, [load, refreshToken]);

  const confirmUnpin = async () => {
    if (!unpinSlot) return;
    setUnpinning(true);
    try {
      await eregApi.clearKeyDocument(protocolId, unpinSlot.role, unpinSlot.variant_label);
      toast('Slot emptied — consumers no longer receive this document');
      setUnpinSlot(null);
      load();
      onChanged?.();
    } catch (err) {
      toast('Failed to empty the slot', { type: 'error' });
    } finally {
      setUnpinning(false);
    }
  };

  const chips = usedByChips(links);
  const liveSlots = slots.filter((s) => s.document && !s.document.deleted_at);

  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Pin className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Key Documents
        </h2>
        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {chips.map((chip) => (
              <span
                key={chip}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-400"
              >
                <Link2 className="h-3 w-3" />
                {chip}
              </span>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
          Loading…
        </div>
      ) : liveSlots.length === 0 ? (
        <p className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
          No key documents pinned yet. Uploading a Protocol, ICF, or IB pins it automatically;
          any document can be pinned from its row menu. Other tools read the current version
          from these slots.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {liveSlots.map((slot) => (
            <li key={slot.id} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-primary-700 dark:text-primary-400">
                  {roleLabels[slot.role] || slot.role}
                  {slot.variant_label !== 'default' ? ` · ${slot.variant_label}` : ''}
                </span>
                <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {slot.document?.document_name}
                  <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-gray-400">
                    v{slot.document?.version_number}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setUnpinSlot(slot)}
                aria-label={`Empty the ${roleLabels[slot.role] || slot.role} slot`}
                title="Empty slot"
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
              >
                <PinOff className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <ConfirmModal
        isOpen={!!unpinSlot}
        onClose={() => setUnpinSlot(null)}
        onConfirm={confirmUnpin}
        title="Empty this key-document slot?"
        message={`Tools that read the current ${unpinSlot ? (roleLabels[unpinSlot.role] || unpinSlot.role) : ''} from this study will stop receiving “${unpinSlot?.document?.document_name || ''}”. You can pin another document at any time.`}
        confirmLabel="Empty slot"
        danger
        loading={unpinning}
      />
    </div>
  );
}

/** Modal used from a document row's "Pin as key document…" action. */
export function PinKeyDocumentModal({ protocolId, document: doc, onClose, onPinned }: {
  protocolId: string;
  document: { id: string; document_name: string; document_type: string };
  onClose: () => void;
  onPinned: () => void;
}) {
  const { toast } = useToast();
  const [role, setRole] = useState<string>('protocol');
  const [variantLabel, setVariantLabel] = useState('default');
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await eregApi.setKeyDocument(protocolId, role, {
        document_id: doc.id,
        variant_label: variantLabel.trim() || 'default',
      });
      toast(`Pinned as current ${STUDY_ROLE_LABELS[role] || role}`);
      onPinned();
    } catch (err: any) {
      toast(err?.response?.data?.error || 'Failed to pin key document', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Pin as key document" maxWidth="max-w-sm">
      <p className="truncate text-sm text-gray-500 dark:text-gray-400" title={doc.document_name}>
        {doc.document_name}
      </p>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300" htmlFor="pin-role">
            Slot
          </label>
          <select
            id="pin-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className={`${fieldCls} w-full`}
          >
            {Object.entries(STUDY_ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300" htmlFor="pin-variant">
            Variant
          </label>
          <input
            id="pin-variant"
            type="text"
            value={variantLabel}
            onChange={(e) => setVariantLabel(e.target.value)}
            placeholder="default"
            className={`${fieldCls} w-full`}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Use variants for parallel current documents — e.g. “spanish” or “sub-study A” ICFs.
          </p>
        </div>
        <div className="flex items-center justify-end gap-3 pt-1">
          <button type="button" onClick={onClose} className={secondaryBtnCls}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className={primaryBtnCls}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Pin
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default KeyDocumentsPanel;
